<template>
  <context-bar-and-content class="pay-referral" v-loading="isLoadingEntity">
    <template slot="context-bar-left">
      <el-button icon="el-icon-arrow-left" @click="cancelPayout">Exit RAF Payout</el-button>
    </template>
    <template slot="context-bar-middle">
      {{ referrerName }}
    </template>
    <template slot="context-bar-right">
      &nbsp;
    </template>
    <div class="main-section">
      <el-row :gutter="18" class="mt-3">
        <el-alert class="mb-3" :title="errorMessage" type="error" show-icon v-if="errorMessage"></el-alert>
        <el-col :span="24" class="mb-3 client-header">
          <h2 class="align-center"><i :class="referrerIcon" /> {{ referrerName }} <span>Referrer</span></h2>
        </el-col>
        <el-col :lg="{ span: 8, offset: 8 }" :md="{ span: 12, offset: 6 }" :sm="{ span: 18, offset: 3 }"
          v-if="!transactionComplete">
          <h2 class="align-center">{{ referralsAvailable.count }} referral(s) totaling {{
            referralsAvailable.formattedAmount
            }} are available to be paid to the business.</h2>
          <el-form ref="payReferral" :model="form" :rules="rules" label-width="150px">
            <el-form-item label="Driver's License" prop="confirmDriversLicense">
              <el-input v-model="form.confirmDriversLicense" placeholder="Business Rep. Driver's License" />
            </el-form-item>
            <el-form-item label="Check Number" prop="checkNumber">
              <el-input v-model="form.checkNumber" placeholder="Check Number" />
            </el-form-item>
            <el-form-item label="Check Amount" prop="checkAmount">
              <el-input v-model="form.checkAmount" placeholder="Check Amount" type="number">
                <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
              </el-input>
            </el-form-item>
          </el-form>
        </el-col>
        <el-col :span="22" :offset="1" class="align-center mt-3" v-if="!transactionComplete">
          <el-button type="primary" icon="el-icon-success" class="submit_btn" :loading="isLoading"
            @click="issueReferralCheck">Issue Referral Check to Business</el-button>
        </el-col>
      </el-row>
      <el-row v-if="transactionComplete">
        <h2 class="align-center">{{ referralsPaid.length }} referral(s) have been paid to the business.</h2>
        <h2 class="align-center">Total ${{ totalPaidAmount }}</h2>
        <p class="align-center">Transaction Id: {{ transactionId }}</p>
        <p class="align-center"><raf-payout-confirmation-pdf-button size="large" :transaction-id="transactionId"
            icon="el-icon-printer">Print Confirmation</raf-payout-confirmation-pdf-button></p>
      </el-row>
      <el-dialog title="Supervisor Approval" :visible.sync="supervisorSignInVisible" :show-close="false"
        :close-on-click-modal="false" :close-on-press-escape="false" width="30%">
        <supervisor-approval-dialog :callback="handleSupervisorApproval"
          @close="supervisorSignInVisible = false"></supervisor-approval-dialog>
      </el-dialog>
    </div>
  </context-bar-and-content>
</template>

<script>
import get from 'lodash/get';
import sumBy from 'lodash/sumBy';
import api from '@/api';
import Business from '@/model/Business';
import ContextBarAndContent from '@/components/layout/ContextBarAndContent.vue';
import { Required, GenericDoubleBlindValidation } from '@/util/formValidators';
import RafPayoutConfirmationPdfButton from '@/components/PDFButton/RAFPayoutConfirmation';
import SupervisorApprovalDialog from '@/components/dialog-content/SupervisorApproval.vue';
import { BusinessReferralPayoutTO, Referral } from '@/model';
import { RAF_STATE } from '@/enum/RAFStatuses';

export default {
  name: 'pay-business-referral',
  data() {
    return {
      client: undefined,
      business: undefined,
      referrals: [],
      supervisorSignInVisible: false,
      isLoading: false,
      transactionComplete: false,
      transactionId: null,
      referralsPaid: [],
      errorMessage: null,
      form: {
        confirmDriversLicense: null,
        checkNumber: null,
        checkAmount: 0.0,
      },
      rules: {
        confirmDriversLicense: [Required, { validator: this.mustMatch('referrer.stateId'), trigger: 'blur' }],
        checkNumber: [Required],
        checkAmount: [Required, { validator: this.validateCheckAmount, trigger: 'blur' }],
      },
      selectedReferrals: [],
      isLoadingEntity: false,
    };
  },
  watch: {
    '$route.params.entityId': {
      immediate: true,
      handler(id) {
        if (id === undefined) {
          return;
        }

        this.initEntityById(id);
      },
    },
  },
  methods: {
    async initEntityById(entityId) {
      this.referrals = [];
      this.business = undefined;

      this.isLoadingEntity = true;
      try {
        const businessResponse = await api.business.get(entityId);
        this.business = new Business(businessResponse.data);

        const referralsResponse = await api.business.getReferrals(entityId);
        this.referrals = referralsResponse.data.map(referral => new Referral(referral));
      } finally {
        this.isLoadingEntity = false;
      }
    },
    cancelPayout() {
      this.$router.push({
        name: 'payReferral',
      });
    },
    async issueReferralCheck() {
      this.errorMessage = null;
      try {
        await this.$refs.payReferral.validate();
      } catch (e) {
        console.warn('form not valid');
        return;
      }

      this.isLoading = true;
      const payoutTO = this.createTO();
      try {
        const response = await api.referral.payBusinessReferral(payoutTO);
        this.transactionComplete = true;
        this.referralsPaid = response.data;
        this.transactionId = this.referralsPaid[0].transactionId;
      } catch (e) {
        if (get(e, 'response.status') === 403) {
          this.supervisorSignInVisible = true;
        } else {
          this.errorMessage = get(e, 'response.data', 'An error occurred.');
        }
      } finally {
        this.isLoading = false;
      }
    },
    async handleSupervisorApproval(token) {
      const payoutTO = this.createTO();
      this.isLoading = true;
      try {
        const response = await api.referral.payBusinessReferralWithApproval(payoutTO, token);
        this.transactionComplete = true;
        this.referralsPaid = response.data;
        this.transactionId = this.referralsPaid[0].transactionId;
      } catch (e) {
        this.errorMessage = get(e, 'response.data', 'An error occurred.');
      } finally {
        this.isLoading = false;
      }
    },
    mustMatch(formItem) {
      return (...args) => {
        GenericDoubleBlindValidation(get(this, formItem), ...args);
      };
    },
    validateCheckAmount(rule, value, callback) {
      if (value <= 0) {
        callback(new Error('Check amount must be greater than zero.'));
        return;
      }

      if (value > this.referralsAvailable.amount) {
        callback(new Error('Check amount exceeds available payout amount.'));
        return;
      }

      if (value % 50 !== 0) {
        callback(new Error('Check amount must be divisble by 50'));
        return;
      }

      callback();
    },
    createTO() {
      return new BusinessReferralPayoutTO({
        businessId: this.business.id,
        stateId: this.form.confirmDriversLicense,
        checkNumber: this.form.checkNumber,
        checkAmount: this.form.checkAmount,
      });
    },
  },
  computed: {
    hasReferrer() {
      return this.referrer !== undefined;
    },
    referrer() {
      return this.business;
    },
    referrerIcon() {
      return 'fa fa-building';
    },
    referrerName() {
      if (this.business !== undefined) {
        return this.business.businessName;
      }
      return '';
    },
    referralsAvailable() {
      const referralsAvailableToPay = this.referrals.filter(referral => !referral.transactionId && referral.status === RAF_STATE.APPROVED);

      const amount = referralsAvailableToPay.reduce((sum, referral) => {
        sum += referral.amount;
        return sum;
      }, 0);
      return {
        count: referralsAvailableToPay.length,
        amount,
        formattedAmount: `$${amount}`,
      };
    },
    totalPaidAmount() {
      return sumBy(this.referralsPaid, 'amount');
    },
  },
  components: {
    ContextBarAndContent,
    RafPayoutConfirmationPdfButton,
    SupervisorApprovalDialog,
  },
};

</script>

<style lang="scss">
.pay-referral {
  .ssn-or-upload-wrapper {
    background: #F2F2F2;
    border-radius: 20px;

    >.el-col:not(:first-child) {
      border-left: 1px solid #CCC;
    }
  }

  .client-header {
    h2 {
      margin: 0;
      padding: 0 5px;
      font-size: 1.8em;

      span {
        font-weight: normal;
        color: #999;
        font-size: 0.75em;
        text-transform: uppercase;
      }
    }
  }

  .main-section {
    width: calc(100% - 18px);
  }
}
</style>
